export interface CartProduct {
  /* The unique ID of the product metadata storyblok page */
  productMetadataPageUniqueId: string;
  name: string
  quantity: number;
  price: string;
}

/**
 * @property preventUpdate - If true, the cart update event won't be triggered
 */
type CartClearParams = {
  preventUpdate: boolean;
};

const errorHandlers: Record<string, (error: Error) => string> = {
  /* eslint-disable @typescript-eslint/naming-convention */
  default: () => 'Unable to perform operation. Please try again.',
  QuotaExceededError: () => 'The cart is full. Please remove some items and try again.',
  SecurityError: () => 'Unable to access cart. Please ensure you are not using private browsing before retrying.',
  /* eslint-enable @typescript-eslint/naming-convention */
};

const handleCartError = (error: Error) => {
  if (error.name in errorHandlers) {
    return errorHandlers[error.name](error);
  }

  return errorHandlers.default(error);
};

const withErrorHandling = <T>(operation: () => T, errorHandling: () => T): Promise<T> => {
  try {
    const result = operation();
    return Promise.resolve(result);
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    CartService.errorMessage = handleCartError(error);
    return Promise.reject(errorHandling());
  }
};

export class CartService {
  public static errorMessage: string;

  public static cartUpdateEvent: string = 'RocheCartUpdate';

  private static readonly cartLocalStorageKey = 'cart';

  public static getCart(): Promise<CartProduct[]> {
    return withErrorHandling(
      () => {
        if (!localStorage.getItem(this.cartLocalStorageKey)) {
          localStorage.setItem(this.cartLocalStorageKey, JSON.stringify([]));
        }
        return JSON.parse(localStorage.getItem(this.cartLocalStorageKey)) as CartProduct[];
      },
      () => [],
    );
  }

  public static addToCart(
    productMetadataPageUniqueId: string, name: string, price: string,
  ): Promise<void> {
    return withErrorHandling(
      async () => {
        const cart = await this.getCart();
        const productInCart = cart
          .find((product) => product.productMetadataPageUniqueId === productMetadataPageUniqueId);

        if (productInCart) {
          productInCart.quantity += 1;
          localStorage.setItem(this.cartLocalStorageKey, JSON.stringify(cart));
          window.dispatchEvent(new Event(this.cartUpdateEvent));
          return;
        }

        localStorage.setItem(
          this.cartLocalStorageKey,
          JSON.stringify([
            ...cart,
            {
            productMetadataPageUniqueId, quantity: 1, name, price,
            },
          ]),
        );
        window.dispatchEvent(new Event(this.cartUpdateEvent));
      },
      () => undefined,
    );
  }

  public static removeFromCart(productMetadataPageUniqueId: string): Promise<void> {
    return withErrorHandling(
      async () => {
        const updatedCart: CartProduct[] = (await this.getCart())
          .filter((item) => item.productMetadataPageUniqueId !== productMetadataPageUniqueId);

        localStorage.setItem(this.cartLocalStorageKey, JSON.stringify(updatedCart));
        window.dispatchEvent(new Event(this.cartUpdateEvent));
      },
      () => undefined,
    );
  }

  public static clearCart(params?: CartClearParams): Promise<void> {
    return withErrorHandling(
      () => {
        localStorage.setItem(this.cartLocalStorageKey, JSON.stringify([]));
        if (params?.preventUpdate) {
          return;
        }
        window.dispatchEvent(new Event(this.cartUpdateEvent));
      },
      () => undefined,
    );
  }

  public static updateQuantity(
    productMetadataPageUniqueId: string,
    quantity: number,
  ): Promise<void> {
    return withErrorHandling(
      async () => {
        if (quantity < 1) {
          return;
        }
        const cart = await this.getCart();
        const productInCart = cart
          .find((product) => product.productMetadataPageUniqueId === productMetadataPageUniqueId);

        if (!productInCart) {
          return;
        }

        productInCart.quantity = quantity;
        localStorage.setItem(this.cartLocalStorageKey, JSON.stringify(cart));
        window.dispatchEvent(new Event(this.cartUpdateEvent));
      },
      () => undefined,
    );
  }
}
